import type {
  Card,
  Evaluation,
  RemoteIdResult,
  SubcategoryAssessment,
  SubcategoryResult,
  Unmet,
} from "../types";
import { cite, sources } from "./sources";
import type { CitationId } from "./sources";
import { classEquivalence, conditions } from "./tables";

/** Conditions shown as warnings; the rest are information. */
const WARNING_CONDITIONS = new Set(["height", "lineOfSight", "airspace"]);

/** Conditions that hold for every flight whatever the answers; shown collapsed. */
const ALWAYS_APPLIES = new Set(
  conditions.map(({ key }) => key).filter((key) => !WARNING_CONDITIONS.has(key)),
);

/** Order of cards within the action group: registration before equipment. */
const ACTION_ORDER = ["subcategory", "scope", "flyer", "operator", "age", "remote-id", "night"];

const HEADINGS: Readonly<Record<string, string>> = {
  flyer: "Flyer ID",
  operator: "Operator ID",
  age: "Operator age",
  subcategory: "Flight category",
  "remote-id": "Remote ID",
  night: "Night lighting",
  scope: "Registration",
};

export type CardGroup = "action" | "flight" | "always";

type OpenEvaluation = Extract<Evaluation, { scope: "open" }>;

export interface Presentation {
  subcategory: SubcategoryResult["fits"];
  cards: Card[];
}

/** Turns engine facts into the cards shown on the page. All wording lives here. */
export function present(evaluation: Evaluation): Presentation {
  if (evaluation.scope === "indoor") {
    return {
      subcategory: null,
      cards: [
        {
          key: "scope",
          level: "info",
          text: "Indoor or securely netted flight: Flyer ID and Operator ID are not required.",
          citation: cite("registration"),
        },
      ],
    };
  }
  if (evaluation.scope === "tooHeavy") {
    return {
      subcategory: null,
      cards: [
        {
          key: "subcategory",
          level: "danger",
          text: `Aircraft of ${evaluation.limitGrams / 1_000}kg or more is outside this Open-category tool.`,
          citation: cite("advanced"),
        },
      ],
    };
  }

  const cards: Card[] = [...registrationCards(evaluation), subcategoryCard(evaluation)];
  cards.push({
    key: "remote-id",
    level:
      evaluation.remoteId.status === "required" || evaluation.remoteId.status === "assumed"
        ? "action"
        : "info",
    text: remoteIdText(evaluation.remoteId),
    citation: cite("remoteId"),
  });
  const light = evaluation.nightLight;
  if (light) {
    const requirement = "a green flashing light fitted to the aircraft and turned on.";
    cards.push({
      key: "night",
      level: light.status === "required" ? "action" : "info",
      text:
        light.status === "required"
          ? `Night flight requires ${requirement}`
          : `From ${light.from}, night flight requires ${requirement}`,
      citation: cite("whereYouCanFly"),
    });
  }
  cards.push(
    ...evaluation.conditions.map((condition): Card => ({
      key: condition.key,
      title: condition.title,
      level: WARNING_CONDITIONS.has(condition.key) ? "warning" : "info",
      text: condition.text,
      citation: cite(condition.citation),
    })),
  );
  return { subcategory: evaluation.subcategory.fits, cards };
}

export function cardHeading(card: Card): string {
  return card.title ?? HEADINGS[card.key] ?? card.key;
}

/**
 * Splits cards into what the user must do, what shapes this flight, and the
 * conditions that always apply. Danger comes first within the action group.
 */
export function groupCards(cards: Card[]): Record<CardGroup, Card[]> {
  const groups: Record<CardGroup, Card[]> = { action: [], flight: [], always: [] };
  for (const card of cards) {
    if (card.level === "danger" || card.level === "action" || card.key === "age")
      groups.action.push(card);
    else if (ALWAYS_APPLIES.has(card.key)) groups.always.push(card);
    else groups.flight.push(card);
  }
  const rank = (card: Card) => (card.level === "danger" ? 0 : 100) + ACTION_ORDER.indexOf(card.key);
  groups.action.sort((left, right) => rank(left) - rank(right));
  return groups;
}

/** One line for the sticky summary, e.g. "A1 · Flyer ID + Operator ID". */
export function summarise({ subcategory, cards }: Presentation): string {
  const actions = groupCards(cards)
    .action.filter(({ level }) => level === "action")
    .map(cardHeading);
  const category =
    subcategory ??
    (cards.some(({ key }) => key === "scope") ? "Indoor flight" : "Not permitted as described");
  return actions.length > 0 ? `${category} · ${actions.join(" + ")}` : category;
}

function registrationCards({ registration }: OpenEvaluation): Card[] {
  const flyerRequired = registration.flyerId === "required";
  const operatorRequired = registration.operatorId === "required";
  const cards: Card[] = [
    {
      key: "flyer",
      level: flyerRequired && !registration.operatorOnly ? "action" : "info",
      text: registration.operatorOnly
        ? `No Flyer ID needed while you do not fly it yourself. ${flyerRequired ? "Anyone who flies it needs their own Flyer ID." : "The CAA recommends one for anyone who flies it."}`
        : flyerRequired
          ? "Flyer ID required."
          : "Flyer ID not required (CAA still recommends competence).",
      citation: cite("registration"),
    },
    {
      key: "operator",
      level: operatorRequired ? "action" : "info",
      text: operatorRequired
        ? `Operator ID required. The registered operator must be ${sources.operatorId.minimumAge} or over; £${sources.operatorId.feeGbp.toFixed(2)} for ${sources.operatorId.validYears} year.`
        : "Operator ID is optional.",
      citation: cite(operatorRequired ? "operator" : "registration"),
    },
  ];
  if (registration.adultOperatorNeeded) {
    cards.push({
      key: "age",
      level: "warning",
      text: "You cannot hold the Operator ID yourself: an adult operator is required.",
      citation: cite("operator"),
    });
  }
  return cards;
}

function subcategoryCard({ aircraft, subcategory }: OpenEvaluation): Card {
  const card = (level: Card["level"], text: string, source: CitationId = "advanced"): Card => ({
    key: "subcategory",
    level,
    text,
    citation: cite(source),
  });
  const find = (id: string) => subcategory.assessments.find((assessment) => assessment.id === id);
  const a2 = find("A2");
  const a2Possible =
    a2 !== undefined && !a2.unmet.some(({ requirement }) => requirement === "aircraft");

  switch (subcategory.fits) {
    case "A1":
      return card("success", "A1: flight over uninvolved people may occur, but never over crowds.");
    case "A2":
      return card(
        "success",
        `A2: keep at least ${a2?.minimums.peopleDistance}m from uninvolved people; never intentionally fly over them. You need a Flyer ID and the A2 Certificate of Competency.`,
      );
    case "A3": {
      const { peopleDistance, areaDistance, buildingDistance } = find("A3")?.minimums ?? {};
      const result = card(
        "success",
        `A3: keep ${peopleDistance}m from uninvolved people, ${areaDistance}m from residential, commercial, industrial or recreational areas, and ${buildingDistance}m from any building.`,
      );
      if (a2 && a2Possible) result.note = `A2 not available: ${reasons(a2).join("; ")}.`;
      return result;
    }
    case null:
      break;
  }

  if (aircraft.origin === "expiredEu")
    return card(
      "danger",
      `EU class marks are treated as their UK equivalent only until ${classEquivalence.until}. This aircraft's subcategory cannot be decided.`,
      "flyLegally",
    );
  if (aircraft.ukClass === "UK5" || aircraft.ukClass === "UK6")
    return card("danger", "UK5 and UK6 aircraft are not flown in the Open subcategories A1 to A3.");
  const eligible = subcategory.assessments.filter(
    ({ unmet }) => !unmet.some(({ requirement }) => requirement === "aircraft"),
  );
  if (
    eligible.every(({ unmet }) => unmet.some(({ requirement }) => requirement === "notOverCrowd"))
  )
    return card("danger", "Flying over a crowd is not permitted in the Open category.");
  const missed = subcategory.assessments.filter(({ id }) => id !== "A1").flatMap(reasons);
  return card("danger", `Not permitted in the Open category as described. ${missed.join("; ")}.`);
}

function reasons({ id, unmet }: SubcategoryAssessment): string[] {
  return unmet.map((item) => `${id} ${reason(id, item)}`);
}

function reason(id: string, { requirement, minimum, actual }: Unmet): string {
  switch (requirement) {
    case "aircraft":
      return id === "A2"
        ? "needs no class mark and under 2kg, or UK2 and under 4kg"
        : "does not cover this aircraft";
    case "notOverCrowd":
      return "never allows flight over a crowd";
    case "a2cofc":
      return "needs the A2 Certificate of Competency";
    case "notOverPeople":
      return id === "A2"
        ? "never allows intentional flight over uninvolved people"
        : "never allows flight over uninvolved people";
    case "peopleDistance":
      return `needs ${minimum}m from uninvolved people, not ${actual}m`;
    case "areaDistance":
      return `needs ${minimum}m from residential, commercial, industrial or recreational areas, not ${actual}m`;
    case "buildingDistance":
      return `needs ${minimum}m from any building, not ${actual}m`;
  }
}

function remoteIdText(result: RemoteIdResult): string {
  switch (result.status) {
    case "required":
      return "Remote ID must be active for this aircraft, linked to the operator's registration.";
    case "upcoming":
      return `Remote ID will be required for this aircraft from ${result.from}.`;
    case "assumed":
      return "EU class marks are no longer recognised, so treat Remote ID as required for this aircraft.";
    case "exempt":
      return "Remote ID not required while the exemption for this aircraft is held.";
    case "notRequired":
      return "No Remote ID obligation identified for this aircraft.";
  }
}
